import { useCallback, useEffect, useMemo, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '../components/Pagination';

const STORAGE_KEY = 'weotp:admin:pageSize';
const DEFAULT_PAGE_SIZE = 25;

const readStoredSize = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const size = Number(raw);
    return PAGE_SIZE_OPTIONS.includes(size) ? size : DEFAULT_PAGE_SIZE;
  } catch (error) {
    return DEFAULT_PAGE_SIZE;
  }
};

/**
 * Rows-per-page preference shared by every admin list, remembered on this
 * browser. Use directly on pages whose API does the paging.
 */
export const usePageSize = () => {
  const [pageSize, setPageSizeState] = useState(readStoredSize);
  const setPageSize = useCallback((size) => {
    const next = PAGE_SIZE_OPTIONS.includes(size) ? size : DEFAULT_PAGE_SIZE;
    setPageSizeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch (error) {
      // Preference just will not persist
    }
  }, []);
  return [pageSize, setPageSize];
};

/**
 * Client-side paging over a list that is already loaded (and filtered).
 *
 *   const pagination = usePagination(filteredHotels);
 *   pagination.items.map(...)            // rows for the current page
 *   <Pagination {...pagination.props} /> // the bar under the table
 *
 * The page resets to 1 whenever the list shrinks below the current page.
 */
const usePagination = (items, { noun } = {}) => {
  const list = Array.isArray(items) ? items : [];
  const [pageSize, setPageSize] = usePageSize();
  const [page, setPage] = useState(1);

  const total = list.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  const safePage = Math.min(page, pages);
  const pageItems = useMemo(
    () => list.slice((safePage - 1) * pageSize, safePage * pageSize),
    [list, safePage, pageSize]
  );

  const changePageSize = useCallback((size) => {
    setPageSize(size);
    setPage(1);
  }, [setPageSize]);

  return {
    items: pageItems,
    page: safePage,
    pages,
    total,
    pageSize,
    setPage,
    setPageSize: changePageSize,
    props: {
      page: safePage,
      pages,
      total,
      pageSize,
      onPageChange: setPage,
      onPageSizeChange: changePageSize,
      noun
    }
  };
};

export default usePagination;
